import { CacheHandler, Fetch, RequestManager } from '@warp-drive/core';
import type { Handler, NextFn } from '@warp-drive/core/request';
import type { RequestContext } from '@warp-drive/core/types/request';
import {
  TioImpersonationHandler,
  type ImpersonationHandlerOptions,
} from './handlers/impersonation-handler.ts';
import { TioResponseHandler, type ResponseHandlerOptions } from './handlers/response-handler.ts';
import { TioDemoHandler } from './handlers/demo-handler.ts';
import { TioTypeNormalizationHandler } from './handlers/type-normalization-handler.ts';
import type { DemoProvider } from './demo/types.ts';
import { authContext } from './auth-context.ts';
import { generateTraceparent } from './utils/traceparent.ts';

const DEFAULT_RENEW_THRESHOLD_MS = 1800000;
const DEFAULT_RENEWAL_COOLDOWN_MS = 5000;
const DEFAULT_RENEWAL_FAILURE_COOLDOWN_MS = 60000;

const defaultTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || '';
const defaultRequestId = () => crypto.randomUUID();

export type TioSessionRenewalOptions = {
  passToken?: boolean;
  thresholdMs?: number;
  cooldownMs?: number;
  failureCooldownMs?: number;
};

/**
 * STATIC configuration only (ADR-001): everything here is environment/config
 * data that never changes after boot. Request-time auth state (token,
 * publicKey, isAuthenticated, expiry, the renew action) is read from
 * `tio-data/auth-context` at request time — never passed in, never captured.
 * That keeps every piece of the chain constructible at module scope.
 */
export type TioRequestManagerOptions = {
  apiKey: string;
  contentType: string;
  accept?: string;
  jsonApiKeyFormat?: string;
  /** Static renewal tuning; the renew ACTION comes from auth-context. */
  sessionRenewal?: TioSessionRenewalOptions;
  timeZone?: () => string;
  requestId?: () => string;
  /** Override the W3C `traceparent` minted per request (tests). */
  traceparent?: () => string;
  /** When set, gates mutating requests during impersonated sessions. */
  impersonation?: ImpersonationHandlerOptions;
  /**
   * Static response-handling policy (which statuses invalidate / toast).
   * Outcomes are delivered via `tio-data/request-events`, not callbacks.
   */
  response?: ResponseHandlerOptions;
  /**
   * When set, a demo handler runs FIRST in the chain (ahead of
   * LegacyNetworkHandler) and short-circuits store-routed requests with
   * scripted fixtures while a demo session is active.
   */
  demo?: DemoProvider;
  /**
   * DEV-only toggle for the `@warp-drive/core` framework-probe patch. The patch lets a small
   * allowlist of Ember/Glimmer duck-typing probes (`isQueryParams`, `unknownProperty`, `toHTML`)
   * return `undefined` instead of throwing the strict "No field named" assertion when framework
   * code reads them off a record — matching classic Model + production behavior. Default `false`
   * (lenient). Set `true` (e.g. from `config/environment.js`) to opt back into the strict assertion
   * for those probes while debugging. No effect in production (the assertion is DEBUG-gated there).
   */
  strictExtensionProbes?: boolean;
};

class TioAuthRequestHandler {
  private nextRenewalAllowedAt = 0;

  constructor(private options: TioRequestManagerOptions) {}

  async request<T>(context: RequestContext, next: NextFn<T>) {
    const { request } = context;
    const headers = new Headers(request.headers);

    // Defaults — `setIfMissing` so a builder that explicitly sets
    // Content-Type (e.g. a JSON-bodied test endpoint inside a vnd.api+json
    // app) wins. Previously these used `.append`, which double-set the
    // header to "<builder>, <default>" and broke the override.
    const setIfMissing = (name: string, value: string) => {
      if (!headers.has(name)) headers.set(name, value);
    };

    setIfMissing('Content-Type', this.options.contentType);
    if (this.options.accept) {
      setIfMissing('Accept', this.options.accept);
    }
    setIfMissing('x-api-key', this.options.apiKey);
    const authKey = authContext.publicKey();
    if (authKey) {
      // `setIfMissing` so a builder that explicitly sets `x-auth-key` (e.g.
      // a special service-to-service flow) still wins over the session's
      // default — same contract as the other headers above.
      setIfMissing('x-auth-key', authKey);
    }
    if (this.options.jsonApiKeyFormat) {
      setIfMissing('x-json-api-key-format', this.options.jsonApiKeyFormat);
    }
    setIfMissing('Time-Zone', this.timeZone());
    setIfMissing('X-Request-Id', this.requestId());
    // W3C trace context: a builder may set its own (e.g. to continue an
    // existing trace); otherwise mint a fresh one for this request.
    setIfMissing('traceparent', this.traceparent());

    // Send any real token we hold — during the post-login boot the session
    // store already has the token before `isAuthenticated` flips true
    // (restore is async), and the legacy adapter sent it in that window.
    // Session RENEWAL still requires a fully restored session.
    const accessToken = authContext.token();
    if (accessToken) {
      headers.set('tio-auth-token', accessToken);
      if (authContext.isAuthenticated()) {
        this.maybeRenewSession();
      }
    }

    return next({ ...request, headers });
  }

  private timeZone() {
    return this.options.timeZone?.() ?? defaultTimeZone();
  }

  private requestId() {
    return this.options.requestId?.() ?? defaultRequestId();
  }

  private traceparent() {
    return this.options.traceparent?.() ?? generateTraceparent();
  }

  private maybeRenewSession() {
    if (!this.options.sessionRenewal) {
      return;
    }

    const now = Date.now();
    const cooldownMs = this.options.sessionRenewal.cooldownMs ?? DEFAULT_RENEWAL_COOLDOWN_MS;
    if (now < this.nextRenewalAllowedAt) {
      return;
    }

    const expiresAt = authContext.sessionExpiresAt();
    if (!expiresAt) {
      return;
    }

    const timeLeft = expiresAt - now;
    const thresholdMs = this.options.sessionRenewal.thresholdMs ?? DEFAULT_RENEW_THRESHOLD_MS;

    if (timeLeft < thresholdMs && timeLeft > 0) {
      this.nextRenewalAllowedAt = now + cooldownMs;

      void (async () => {
        try {
          if (!authContext.isAuthenticated()) {
            return;
          }

          const currentToken = authContext.token();
          if (!currentToken) {
            return;
          }

          if (!authContext.hasRenew()) {
            throw new Error(
              'Session renewal is configured but no renew provider is installed in auth-context'
            );
          }

          if (this.options.sessionRenewal?.passToken === false) {
            await authContext.renew();
          } else {
            await authContext.renew(currentToken);
          }
        } catch (error) {
          const failureCooldownMs =
            this.options.sessionRenewal?.failureCooldownMs ?? DEFAULT_RENEWAL_FAILURE_COOLDOWN_MS;
          this.nextRenewalAllowedAt = Date.now() + failureCooldownMs;
          console.error('Failed to renew session:', error);
        }
      })();
    }
  }
}

export function createTioRequestManager(options: TioRequestManagerOptions) {
  return new RequestManager()
    .use([...createTioRequestHandlers(options), Fetch])
    .useCache(CacheHandler);
}

/**
 * The Tio handler chain WITHOUT the terminal Fetch handler — for store
 * setups that compose their own manager (e.g. `useLegacyStore({ handlers })`,
 * which appends Fetch/CacheHandler itself). `createTioRequestManager` is the
 * complete-manager convenience wrapper around this.
 *
 * Owner-free by design (ADR-001): every handler is constructed from static
 * config and reads request-time state from auth-context. Safe at module
 * scope.
 */
export function createTioRequestHandlers(options: TioRequestManagerOptions) {
  // Order matters. The demo handler (when configured) runs FIRST so it can
  // intercept both legacy adapter ops and builder requests before
  // LegacyNetworkHandler claims them — it short-circuits with fixtures while a
  // demo session is active and is a transparent pass-through otherwise.
  // LegacyNetworkHandler then short-circuits adapter-routed requests (they keep
  // using ApplicationAdapter#ajax/handleResponse). For builder requests, the
  // response handler wraps everything below it so it can observe errors thrown
  // by Fetch; the impersonation handler then gates the outgoing request before
  // auth headers are applied.
  const handlers: Handler[] = [];
  if (options.demo) {
    handlers.push(new TioDemoHandler(options.demo));
  }
  if (options.response) {
    handlers.push(new TioResponseHandler(options.response));
  }
  if (options.impersonation) {
    handlers.push(new TioImpersonationHandler(options.impersonation));
  }
  // Normalizes JSON:API type keys on builder-read responses to the schema
  // format before cache insertion (the modern home of the legacy
  // serializer's modelNameFromPayloadKey) — see the handler's docblock.
  handlers.push(new TioTypeNormalizationHandler(options.jsonApiKeyFormat));
  handlers.push(new TioAuthRequestHandler(options));
  return handlers;
}
